/**
 * @file
 * State manager for the layout plan component.
 *
 * @description
 * Replaces React's useState / useContext with a plain class that holds
 * the navigation history, selection state, and hover state.
 * Emits granular callbacks so the renderer can perform surgical DOM updates.
 */

import type {
  NormalizedPolygon,
  NormalizedLevel,
  NormalizedRootLayer,
  BreadcrumbItem,
  LayoutPlanCallbacks,
} from './_types';

interface HistoryEntry extends NormalizedLevel {
  via?: { id: string; label: string };
}

export class LayoutPlanStateManager {
  private history: HistoryEntry[];
  private _selectedId: string | null = null;
  private _hoverId: string | null = null;
  private _activeLayerIndex = 0;
  private layers: NormalizedRootLayer[];
  private projectName: string;
  private callbacks: LayoutPlanCallbacks;

  constructor(layers: NormalizedRootLayer[], projectName: string, callbacks: LayoutPlanCallbacks) {
    this.projectName = projectName;
    this.callbacks = callbacks;
    this.layers = layers;
    this.history = [this.entryForLayer(0)];
  }

  /** Build the level entry for a top-level plan. */
  private entryForLayer(index: number): HistoryEntry {
    const layer: NormalizedLevel | undefined = this.layers[index];
    return { backgroundUrl: layer?.backgroundUrl ?? null, polygons: layer?.polygons ?? [] };
  }

  // ---------------------------------------------------------------------------
  // Getters
  // ---------------------------------------------------------------------------

  private get currentLevel(): HistoryEntry {
    return this.history[this.history.length - 1];
  }

  get backgroundUrl(): string | null {
    return this.currentLevel.backgroundUrl;
  }

  get polygons(): NormalizedPolygon[] {
    return this.currentLevel.polygons;
  }

  get selectedId(): string | null {
    return this._selectedId;
  }

  get hoverId(): string | null {
    return this._hoverId;
  }

  get canGoBack(): boolean {
    return this.history.length > 1;
  }

  /** The top-level plans available for this project. */
  get rootLayers(): NormalizedRootLayer[] {
    return this.layers;
  }

  get activeLayerIndex(): number {
    return this._activeLayerIndex;
  }

  get selectedPolygon(): NormalizedPolygon | null {
    if (!this._selectedId) return null;
    return this.polygons.find((p) => p.id === this._selectedId) ?? null;
  }

  get breadcrumbs(): BreadcrumbItem[] {
    const trail: BreadcrumbItem[] = [{ id: 'root', label: this.projectName }];
    for (let i = 1; i < this.history.length; i++) {
      const entry = this.history[i];
      if (entry.via) {
        trail.push({ id: entry.via.id, label: entry.via.label });
      }
    }
    // Append the selected property name as the final breadcrumb.
    const selected = this.selectedPolygon;
    if (selected?.property) {
      trail.push({ id: selected.id, label: selected.name });
    }
    return trail;
  }

  // ---------------------------------------------------------------------------
  // Mutations
  // ---------------------------------------------------------------------------

  select(id: string | null): void {
    if (id === this._selectedId) return;
    const oldId = this._selectedId;
    this._selectedId = id;
    this.callbacks.onSelectChange(oldId, id);
  }

  hover(id: string | null): void {
    if (id === this._hoverId) return;
    const oldId = this._hoverId;
    this._hoverId = id;
    this.callbacks.onHoverChange(oldId, id);
  }

  /**
   * Drill into a polygon's child layer.
   * If the polygon is not found or has no children, this is a no-op.
   */
  drillDown(polygonId: string): void {
    const polygon = this.polygons.find((p) => p.id === polygonId);
    if (!polygon?.childLayers?.length) return;

    const layer = polygon.childLayers[0];
    this.history.push({
      backgroundUrl: layer.backgroundUrl,
      polygons: layer.polygons,
      via: { id: polygon.id, label: polygon.name },
    });
    this._selectedId = null;
    this._hoverId = null;
    this.callbacks.onNavigate();
  }

  /** Go back one level in the drill-down history. */
  goBack(): void {
    if (this.history.length <= 1) return;
    this.history.pop();
    this._selectedId = null;
    this._hoverId = null;
    this.callbacks.onNavigate();
  }

  /**
   * Switch to another top-level plan, resetting the drill-down history.
   *
   * Out-of-range indexes are no-ops. Re-selecting the active plan is a no-op
   * only while already at its top level; after drilling in, it returns the
   * visitor to that top level so the highlighted button is never inert.
   */
  selectLayer(index: number): void {
    if (index < 0 || index >= this.layers.length) return;
    if (index === this._activeLayerIndex && this.history.length === 1) return;

    this._activeLayerIndex = index;
    this.history = [this.entryForLayer(index)];
    this._selectedId = null;
    this._hoverId = null;
    this.callbacks.onNavigate();
  }

  /** Jump to a specific depth in the breadcrumb trail. */
  navigateToDepth(depth: number): void {
    if (depth < 0 || depth >= this.history.length) return;
    this.history.length = depth + 1;
    this._selectedId = null;
    this._hoverId = null;
    this.callbacks.onNavigate();
  }

  /**
   * Handle a click on a polygon (or its sidebar button).
   * If the polygon has children, drill down immediately.
   * Otherwise, toggle selection.
   */
  handleShapeClick(id: string): void {
    const polygon = this.polygons.find((p) => p.id === id);
    if (!polygon || !polygon.isActive) return;

    if (polygon.childLayers?.length) {
      this.drillDown(id);
    } else if (this._selectedId === id) {
      this.select(null);
    } else {
      this.select(id);
    }
  }

  /**
   * Returns the label of the current drill-down level,
   * or null if at root.
   */
  get currentLevelLabel(): string | null {
    const entry = this.currentLevel;
    return entry.via?.label ?? null;
  }
}
